import {isEqual} from 'lodash';
import {memo, MouseEvent, useCallback, useMemo} from 'react';
import {SubwayData} from '@/map/@types';
import {
  AROUND_DATA_OPTIONS, MAX_VISIBLE_ZOOM_METER, MIN_SUBWAY_PASSENGERS_COUNT, SUBWAY_LINE_COLORS
} from '@/map/constants';
import {AreaAnalyticsFilter} from '@/map/enums';
import {useVisibleZoomLevel} from '@/map/hooks';
import {convertToCoordFromPointCoords} from '@/map/utils';
import {SpotMarker} from '../../Marker';

const markerType = AreaAnalyticsFilter.SUBWAY;
const typeInfo = AROUND_DATA_OPTIONS[markerType];

interface IProps {
  map?: naver.maps.Map;
  items?: SubwayData[];
  selectedItem?: SubwayData;
  onClick?(selected: {type: typeof markerType; index: number}): void;
}

interface MarkerProps {
  map: naver.maps.Map;
  id: string;
  item: SubwayData;
  active?: boolean;
  onClick?(e: MouseEvent<HTMLDivElement>): void;
}

export const SubwayStationMarkers = ({
  map,
  items,
  selectedItem,
  onClick,
}: IProps) => {
  const isVisibleZoom = useVisibleZoomLevel(
    MAX_VISIBLE_ZOOM_METER.mobile_markers
  );

  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const id = e.currentTarget.dataset.id;

      if (id) {
        onClick?.({type: markerType, index: Number(id)});
      }
    },
    [onClick]
  );

  if (!map || !items || !isVisibleZoom) {
    return null;
  }

  return (
    <>
      {items.map((i, idx) => (
        <Marker
          key={idx}
          map={map}
          id={String(idx)}
          item={i}
          active={isEqual(selectedItem, i)}
          onClick={handleClick}
        />
      ))}
    </>
  );
};

const Marker = memo(({item, ...props}: MarkerProps) => {
  const position = useMemo(
    () => convertToCoordFromPointCoords(item?.location?.coordinates),
    [item?.location?.coordinates]
  );
  const subway = useMemo(() => {
    const pick = item?.subways?.[0];

    if (!pick?.getOnOffAvg || pick.getOnOffAvg > MIN_SUBWAY_PASSENGERS_COUNT) {
      return pick;
    }
  }, [item?.subways]);

  if (!item || !position || !subway) {
    return null;
  }

  return (
    <SpotMarker
      {...props}
      position={position}
      name={`${subway.line} ${subway.name}`}
      color={SUBWAY_LINE_COLORS[subway.line]}
      typeLabel={typeInfo.label}
      icon={typeInfo.mobile?.icon}
    />
  );
});
